import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { PageMeta } from "@/components/PageMeta";

export function CategoryGuide() {
  const [, setLocation] = useLocation();
  const navToSignIn = () => setLocation("/sign-in");

  return (
    <div className="min-h-screen bg-[#030913] text-white font-sans selection:bg-[#7C6EEF]/30 pb-20">
      <PageMeta
        title="What Is an Autonomous AI Growth Platform? — LaunchTactix"
        description="A complete guide to Autonomous AI Growth Platforms: how 70+ AI modules replace a full growth team for lead generation, SEO, outreach, and pipeline."
        canonical="https://launchtactix.com/what-is-autonomous-ai-growth-platform"
      />
      <nav className="sticky top-0 z-50 bg-[#030913]/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-6 py-4">
        <button onClick={() => setLocation("/")} className="hover:opacity-80 transition-opacity">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-8 w-auto mix-blend-screen" />
        </button>
        <button
          onClick={navToSignIn}
          className="text-xs font-bold tracking-widest text-white/70 hover:text-white transition-colors uppercase px-5 py-2 border border-white/10 hover:bg-white/5 rounded-none"
        >
          INITIATE
        </button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 text-center"
        >
          <p className="font-mono text-sm tracking-widest text-[#7C6EEF] mb-6 uppercase">// CATEGORY GUIDE</p>
          <h1 className="text-5xl md:text-7xl font-display font-bold uppercase tracking-tight mb-8">What is an autonomous AI growth platform?</h1>
          <p className="text-lg text-white/60 leading-relaxed max-w-3xl mx-auto">
            The definitive guide to autonomous B2B growth infrastructure — what it is, how it works, and why it's replacing traditional growth teams.
          </p>
          <div className="mt-10">
            <button
              onClick={navToSignIn}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
            >
              LAUNCH ENGINE →
            </button>
          </div>
        </motion.div>

        {/* SECTION 1 — DEFINITION */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 prose prose-invert prose-p:text-white/60 prose-p:leading-relaxed max-w-none"
        >
          <h2 className="text-3xl font-display font-bold uppercase text-white/90 mb-8 border-b border-white/10 pb-4">What is an autonomous AI growth platform?</h2>
          
          <p>
            An autonomous AI growth platform is a unified software system that performs every function of a traditional B2B growth team — lead discovery, outreach, enrichment, pipeline management, content, and retention — without human execution. Unlike a point tool that automates one task, an autonomous AI growth platform connects every growth function into a single, continuously self-optimizing system.
          </p>

          <p>
            The defining characteristic is autonomy: the platform makes decisions, adapts to data, and executes tasks end-to-end without requiring human input at each step. It discovers leads that match your ICP, verifies their contact details, personalizes and sends outreach, handles replies, books meetings, tracks deals, publishes content, and monitors account health — all simultaneously, all the time.
          </p>

          <p>
            Traditional growth stacks require humans to connect the dots between tools, interpret data, and trigger next steps. An autonomous AI growth platform does this automatically — the intelligence layer is built in.
          </p>
        </motion.section>

        {/* SECTION 2 — FOUR CHARACTERISTICS */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="mb-12 border-b border-white/10 pb-4">
            <h2 className="text-3xl font-display font-bold uppercase text-white/90 mb-2">The four defining characteristics</h2>
            <p className="text-white/50">What separates a true autonomous AI growth platform from a collection of automation tools.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/[0.02] border border-white/10 p-8">
              <div className="text-[#7C6EEF] font-mono text-xs tracking-widest mb-4">1</div>
              <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">END-TO-END AUTONOMY</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                A true autonomous AI growth platform doesn't just automate individual tasks — it connects every stage of the growth process into a continuous loop. Lead discovery feeds outreach. Outreach results feed scoring. Scoring feeds prioritization. Meeting outcomes feed retention. Nothing requires a human to connect the stages.
              </p>
            </div>

            <div className="bg-white/[0.02] border border-white/10 p-8">
              <div className="text-[#7C6EEF] font-mono text-xs tracking-widest mb-4">2</div>
              <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">SELF-OPTIMIZING INTELLIGENCE</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                The platform continuously analyzes its own performance and adjusts. Sequences that underperform are modified. Leads that convert become the basis for tighter ICP models. Deliverability dips trigger automatic mailbox rotation. The system gets smarter with every cycle — without human tuning.
              </p>
            </div>

            <div className="bg-white/[0.02] border border-white/10 p-8">
              <div className="text-[#7C6EEF] font-mono text-xs tracking-widest mb-4">3</div>
              <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">UNIFIED DATA LAYER</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                All growth data — leads, emails, replies, meetings, deals, content performance, retention signals — lives in a single connected data layer. Attribution is automatic. Every deal traces back to its originating outreach sequence. Every at-risk account surfaces before it churns. No data silos, no manual attribution.
              </p>
            </div>

            <div className="bg-white/[0.02] border border-white/10 p-8">
              <div className="text-[#7C6EEF] font-mono text-xs tracking-widest mb-4">4</div>
              <h3 className="text-lg font-display font-bold uppercase tracking-wide text-white mb-3">INFRASTRUCTURE-GRADE RELIABILITY</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                An autonomous AI growth platform runs 24/7 — not just during business hours. It handles compliance automatically (unsubscribe processing, bounce management, suppression lists), monitors its own deliverability health, and operates at scale without degradation. It's infrastructure, not software.
              </p>
            </div>
          </div>
        </motion.section>

        {/* SECTION 3 — STATS */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 py-16 bg-[#7C6EEF]/5 border-y border-[#7C6EEF]/20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">42+</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">AI modules in LaunchTactix</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">1,200+</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">businesses using autonomous growth</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">24/7</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">autonomous operation</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display font-bold text-white mb-2">4,973</div>
              <div className="text-[10px] font-mono tracking-widest text-[#7C6EEF] uppercase">leads generated this month</div>
            </div>
          </div>
        </motion.section>

        {/* SECTION 4 — FAQ */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="mb-12 border-b border-white/10 pb-4">
            <h2 className="text-3xl font-display font-bold uppercase text-white/90 mb-2">Autonomous AI growth platform questions</h2>
            <p className="text-white/50">Common questions about how autonomous AI growth platforms work.</p>
          </div>

          <div className="space-y-8">
            <div className="bg-white/[0.02] p-8 border border-white/5">
              <h3 className="font-display font-bold uppercase text-white tracking-wide mb-4">Q: How is an autonomous AI growth platform different from a marketing automation tool?</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                A: Marketing automation tools (like Mailchimp, HubSpot, or Klaviyo) automate the sending of pre-written sequences you build and maintain manually. An autonomous AI growth platform generates the content, discovers the leads, verifies the data, adapts the sequences in real time, and manages the entire funnel end-to-end — without you building or maintaining the flows. The distinction is between automating a task you designed versus deploying an autonomous system that executes the entire strategy.
              </p>
            </div>

            <div className="bg-white/[0.02] p-8 border border-white/5">
              <h3 className="font-display font-bold uppercase text-white tracking-wide mb-4">Q: Does an autonomous AI growth platform replace CRM software?</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                A: A full autonomous AI growth platform includes CRM functionality as part of the unified system — deal tracking, contact management, pipeline stages, and meeting scheduling. The difference from a standalone CRM is that the pipeline is populated automatically from outreach sequences, deals are attributed to specific campaigns, and the system continues to nurture and score accounts without manual input.
              </p>
            </div>

            <div className="bg-white/[0.02] p-8 border border-white/5">
              <h3 className="font-display font-bold uppercase text-white tracking-wide mb-4">Q: What size company benefits most from an autonomous AI growth platform?</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                A: Autonomous AI growth platforms deliver the highest ROI for companies with 1–50 employees — specifically, those who need enterprise-grade growth output without enterprise-sized teams. Solo founders can operate like a 10-person growth team. Agencies can run growth infrastructure for multiple clients from a single platform. Larger companies use them to augment their existing teams and remove execution bottlenecks.
              </p>
            </div>

            <div className="bg-white/[0.02] p-8 border border-white/5">
              <h3 className="font-display font-bold uppercase text-white tracking-wide mb-4">Q: Is an autonomous AI growth platform the same as an AI SDR?</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                A: No. An AI SDR is a point tool that automates one function: outreach. An autonomous AI growth platform covers the entire revenue generation lifecycle — discovery, verification, outreach, reply handling, meeting booking, pipeline management, content, SEO, social, and retention. An AI SDR is one module. An autonomous AI growth platform is the entire infrastructure.
              </p>
            </div>
          </div>
        </motion.section>

        {/* BOTTOM CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center border-t border-white/5 pt-20"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold uppercase tracking-tight mb-6">See an autonomous AI growth platform in action.</h2>
          <p className="text-lg text-white/50 mb-10">LaunchTactix is the complete autonomous AI growth platform — 70+ modules, one system, zero headcount required.</p>
          <button
            onClick={navToSignIn}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
          >
            LAUNCH ENGINE →
          </button>
        </motion.div>
      </main>

      <footer className="mt-32 py-12 px-6 border-t border-white/5 bg-black">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-6 w-auto mix-blend-screen opacity-50" />
          <p className="text-[10px] font-mono tracking-widest text-white/30 uppercase">© {new Date().getFullYear()} LaunchTactix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
